import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request } from '@/lib/http';

export { ApiError };

/**
 * User-controlled UI preferences, stored server-side in user_preferences.
 * `null` for either field means "no override stored" — the client falls back
 * to its own default ('system' theme, navigator language).
 */
export interface UserPreferences {
  theme: 'light' | 'dark' | 'system' | null;
  language: 'en' | 'pl' | 'system' | null;
}

export interface Me {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: 'USER' | 'ADMIN';
  preferences: UserPreferences;
  /** Whether invoice issuing (the draft composer + issue flow) is enabled for this deployment. */
  issuingEnabled: boolean;
}

export type PreferencesPatch = Partial<{
  theme: 'light' | 'dark' | 'system';
  language: 'en' | 'pl' | 'system';
}>;

export const ME_QUERY_KEY = ['auth', 'me'] as const;

export function useMe() {
  return useQuery<Me | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        return await request<Me>('/api/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useRequestOtp() {
  return useMutation({
    mutationFn: (email: string) =>
      request<{ ok: boolean; otpLength: number | null }>('/api/auth/request', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
  });
}

export function useVerifyOtp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, otp }: { email: string; otp: string }) =>
      request<Me>('/api/auth/verify', {
        method: 'POST',
        body: JSON.stringify({ email, otp }),
      }),
    onSuccess: (me) => {
      qc.setQueryData(ME_QUERY_KEY, me);
    },
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PreferencesPatch) =>
      request<UserPreferences>('/api/me/preferences', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (updated) => {
      // Keep ME_QUERY_KEY in sync so any subscriber re-renders without a refetch.
      qc.setQueryData<Me | null>(ME_QUERY_KEY, (prev) =>
        prev ? { ...prev, preferences: updated } : prev,
      );
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => request<void>('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData(ME_QUERY_KEY, null);
      qc.removeQueries({
        predicate: (query) => query.queryKey[0] !== ME_QUERY_KEY[0],
      });
    },
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { confirmEmail: string }>({
    mutationFn: (body) => request<void>('/api/me', { method: 'DELETE', body: JSON.stringify(body) }),
    onSuccess: () => {
      // Mirror logout: the session is gone server-side, so drop the cached user and
      // every other query, which sends the app back to the login screen.
      qc.setQueryData(ME_QUERY_KEY, null);
      qc.removeQueries({
        predicate: (query) => query.queryKey[0] !== ME_QUERY_KEY[0],
      });
    },
  });
}
